import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Monitor, Smartphone, TrendingUp, Zap, FileText, Mail } from 'lucide-react';

const WebsiteDesign = () => {
  const benefits = [
    "Looks professional and premium",
    "Loads fast and works on mobile",
    "Communicates your value clearly",
    "Drives calls, bookings, and inquiries",
    "Supports your automation and marketing systems"
  ];

  const websiteTypes = [
    "Service business websites",
    "Consulting and professional services sites",
    "Nonprofit sites with donation flows",
    "Booking-based businesses (appointments, estimates, consults)",
    "Landing pages for offers and campaigns"
  ];

  const coreDeliverables = [
    "Strategy + site structure (what pages you need and why)",
    "Luxury-tech design style (clean, modern, premium)",
    "Mobile-first layouts",
    "Conversion-focused copy structure (headlines, CTAs, sections)",
    "SEO-ready setup (titles, descriptions, headings)",
    "Contact forms + lead capture",
    "Booking integration (Calendly/TidyCal or your preferred tool)",
    "Analytics setup (Google Analytics + basic tracking)",
    "Basic performance optimization"
  ];

  const addOns = [
    "Blog / resource library setup",
    "Email marketing integration (ConvertKit/Mailchimp)",
    "Lead magnet setup + PDF downloads",
    "Additional landing pages",
    "Ongoing website maintenance & updates"
  ];

  const packages = [
    {
      name: "Starter Website",
      subtitle: "3–5 pages",
      price: "$1,200–$3,000",
      description: "Perfect for businesses that need a polished online presence fast.",
      features: [
        "Home, Services, About, Contact, Booking/CTA",
        "Mobile optimization",
        "Forms + booking integration"
      ]
    },
    {
      name: "Growth Website",
      subtitle: "5–8 pages",
      price: "$3,000–$6,500",
      description: "For businesses ready to scale with stronger messaging and deeper structure.",
      highlight: true,
      features: [
        "Everything in Starter plus:",
        "Industry-specific service pages",
        "Case studies/portfolio page",
        "SEO optimization support",
        "Stronger conversion flow"
      ]
    },
    {
      name: "Premium Build + Funnel System",
      subtitle: "8+ pages",
      price: "$6,500–$10,000+",
      description: "For brands that want a premium site plus automated lead capture and follow-up.",
      features: [
        "Everything in Growth plus:",
        "Offer landing pages",
        "Lead magnet funnel",
        "Email follow-up automation",
        "Full conversion strategy"
      ]
    }
  ];

  const howItWorks = [
    { step: 1, title: "Strategy Call", description: "Goals, audience, offer" },
    { step: 2, title: "Blueprint", description: "Site map + conversion flow" },
    { step: 3, title: "Design + Build", description: "Luxury-tech layout + structure" },
    { step: 4, title: "Copy + Integration", description: "Booking, forms, tracking" },
    { step: 5, title: "Launch + Support", description: "QA + handoff" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F5] via-white to-[#F5F5F5]"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#227AFF]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6">
              Website Design & <span className="text-gradient">Rebuild</span>
            </h1>
            <p className="text-2xl font-semibold text-gray-700 mb-8">
              Luxury-Tech Websites Built to Convert
            </p>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Your website should do more than look good — it should build trust, clearly explain what you do, and convert visitors into booked calls, leads, and customers.
            </p>
            <p className="text-xl font-semibold text-gray-700 mb-8">
              We build modern websites that feel premium and perform like a sales engine.
            </p>
            <Link to="/contact" className="btn btn-primary text-lg px-8">
              Book a Free Website Strategy Call
            </Link>
          </div>
        </div>
      </section>

      {/* Why Your Website Matters */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">Why Your Website Matters</h2>
            <div className="accent-line"></div>
            <p className="text-xl text-center text-gray-600 mb-12 mt-6">
              For many customers, your website is your first impression. If it's outdated, unclear, slow, or hard to navigate, people leave — and they rarely come back.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold mb-6 text-center">We help you create a website that:</h3>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-[#227AFF] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Build */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">What We Build</h2>
            <div className="accent-line"></div>
            <p className="text-xl text-center text-gray-600 mb-12 mt-6">
              We specialize in websites for small and medium businesses that want clarity, credibility, and conversions.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold mb-6">Website Types</h3>
              <div className="space-y-3">
                {websiteTypes.map((type, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#227AFF] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-center mb-4">What's Included</h2>
            <div className="accent-line"></div>
            
            <div className="grid md:grid-cols-2 gap-8 mt-12">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-semibold mb-6">Core Deliverables</h3>
                <div className="space-y-3">
                  {coreDeliverables.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#227AFF] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-semibold mb-6">Optional Add-Ons</h3>
                <div className="space-y-3">
                  {addOns.map((addon, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-[#227AFF] mr-3 text-lg">•</span>
                      <span className="text-sm text-gray-700">{addon}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-center mb-4">Website Packages</h2>
            <div className="accent-line"></div>
            <p className="text-xl text-center text-gray-600 mb-12 mt-6">
              Choose the package that matches your business needs
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {packages.map((pkg, index) => (
                <div
                  key={index}
                  className={`card ${pkg.highlight ? 'border-2 border-[#227AFF] relative' : ''}`}
                >
                  {pkg.highlight && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-[#227AFF] text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-1">{pkg.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{pkg.subtitle}</p>
                  <p className="text-3xl font-bold text-[#227AFF] mb-4">{pkg.price}</p>
                  <p className="text-gray-600 mb-6">{pkg.description}</p>
                  <div className="mb-6">
                    <h4 className="font-semibold text-sm mb-3">Includes:</h4>
                    <ul className="space-y-2">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-[#227AFF] mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link
                    to="/contact"
                    className={`btn w-full ${
                      pkg.highlight ? 'btn-primary' : 'btn-secondary'
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">How It Works</h2>
            <div className="accent-line"></div>
            
            <div className="grid md:grid-cols-5 gap-6 mt-12">
              {howItWorks.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#227AFF] text-white font-bold text-xl mb-4">
                    {item.step}
                  </div>
                  <h4 className="font-semibold mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-6 text-white">Ready for a website that looks premium and drives results?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's build a site that supports your business growth and pairs perfectly with AI automation.
            </p>
            <Link to="/contact" className="btn btn-accent text-lg px-8 animate-glow">
              Book a Free Website Strategy Call
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebsiteDesign;